import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

const getPageNumbers = (current, total) => {
  if (total <= 1) return [1];
  const delta = 1;
  const range = [];
  const withDots = [];
  let last;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (last) {
      if (i - last === 2) withDots.push(last + 1);
      else if (i - last !== 1) withDots.push("...");
    }
    withDots.push(i);
    last = i;
  });

  return withDots;
};

const InventoryPagination = ({
  page,
  perPage,
  total,
  isFetching = false,
  onPageChange,
  onPerPageChange,
}) => {
  const lastPage = Math.max(1, Math.ceil(total / perPage) || 1);
  const currentPage = Math.min(Math.max(1, page), lastPage);
  const from = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = Math.min(currentPage * perPage, total);
  const pages = getPageNumbers(currentPage, lastPage);

  return (
    <div className="flex flex-col gap-3 px-4 py-3 border-t border-gray-100 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap gap-3 items-center text-sm text-gray-500">
        <span>
          Showing{" "}
          <span className="font-medium text-gray-700">{from}</span> to{" "}
          <span className="font-medium text-gray-700">{to}</span> of{" "}
          <span className="font-medium text-gray-700">{total}</span> products
        </span>
        <span className="hidden text-gray-300 sm:inline">|</span>
        <label className="flex gap-1.5 items-center text-xs text-gray-500">
          Rows per page
          <select
            value={perPage}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            className="px-2 py-1 text-xs bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex gap-1.5 items-center">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1 || isFetching}
          className="inline-flex justify-center items-center w-8 h-8 text-gray-600 rounded-lg border border-gray-200 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {pages.map((p, idx) =>
          p === "..." ? (
            <span key={`dots-${idx}`} className="px-1 text-gray-400">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              disabled={isFetching}
              className={`inline-flex justify-center items-center min-w-[2rem] h-8 px-2 text-sm rounded-lg border transition-colors disabled:opacity-40 ${
                p === currentPage
                  ? "border-[#FF6C6C] bg-[#FF6C6C] text-white"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(Math.min(currentPage + 1, lastPage))}
          disabled={currentPage === lastPage || isFetching}
          className="inline-flex justify-center items-center w-8 h-8 text-gray-600 rounded-lg border border-gray-200 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InventoryPagination;
